import axios from 'axios';

import { getDisplayTimezone } from 'src/utils/format-time';

import { CONFIG } from 'src/config-global';

const axiosInstance = axios.create({
  baseURL: CONFIG.serverUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

// request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Get token from localStorage and add to headers (only on client side)
    if (typeof window !== 'undefined') {
      const accessToken = localStorage.getItem('jwt_access_token');
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      // Viewing timezone as a fallback hint; write payloads always carry an
      // explicit `timezone` field (spec §29), this only informs defaults.
      const tz = getDisplayTimezone();
      if (tz) {
        config.headers['X-Timezone'] = tz;
      }
    }

    // Dev-only, and never the Authorization header — it is a live session token
    // and anything printed here is readable by every browser extension on the
    // tab, captured by screen recordings, and picked up as breadcrumbs by error
    // trackers. import.meta.env.DEV is statically false in a production build,
    // so this block is tree-shaken out.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('Axios Request:', config.method?.toUpperCase(), config.url, config.params ?? '');
    }
    return config;
  },
  (error) => {
    if (import.meta.env.DEV) {
      console.error('Axios Request Error:', error);
    }
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    // Response bodies carry user records, payments and chat messages — dev only.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(`Axios Response of ${response.config.url}`, response);
    }
    return response;
  },
  (error) => {
    if (import.meta.env.DEV) {
      console.error(`Axios Error of ${error.config?.url}`, error);
    }

    // Handle different error response formats
    let errorMessage = 'Something went wrong!';

    if (error.response?.data) {
      const { data } = error.response;
      if (data.message) {
        errorMessage = data.message;
      } else if (typeof data === 'string') {
        errorMessage = data;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    if (import.meta.env.DEV) {
      console.error('Error Message:', errorMessage);
    }

    // 401 means the session is no longer valid (token expired, account suspended,
    // deleted, or made inactive). Clear local state and bounce to sign-in.
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      const isOnSignIn = window.location.pathname.includes('/sign-in');
      localStorage.removeItem('jwt_access_token');
      localStorage.removeItem('admin_user');
      if (!isOnSignIn) {
        window.location.href = '/auth/jwt/sign-in';
      }
    }

    const errorObj = new Error(errorMessage);
    // Preserve the HTTP status so callers can distinguish 404 / 403 from network errors.
    errorObj.status = error.response?.status;
    // Field-level validation details (see src/utils/api-error.js). The backend nests
    // them under the envelope's `data`; tolerate a top-level shape too.
    const body = error.response?.data;
    if (body && typeof body === 'object') {
      const nested = body.data && typeof body.data === 'object' ? body.data : null;
      errorObj.code = nested?.code ?? body.code;
      errorObj.errors = nested?.errors ?? body.errors;
      errorObj.data = body;
    }
    return Promise.reject(errorObj);
  }
);

export default axiosInstance;

export const fetcher = async (args) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];
    const res = await axiosInstance.get(url, { ...config });

    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

export const endpoints = {
  auth: {
    me: 'admin/auth/me',
    signIn: 'admin/auth/sign-in',
    signUp: 'admin/auth/sign-up',
    adminSignIn: 'admin/auth/login',
    profile: 'users/profile',
    editProfile: 'users/edit-profile',
    changePassword: 'users/change-password',
    registerDevice: 'auth/devices/register',
  },
  clubs: {
    list: 'admin/clubs',
    create: 'admin/clubs/add',
    get: (id) => `admin/clubs/${id}`,
    update: (id) => `admin/clubs/${id}`,
    delete: (id) => `admin/clubs/${id}`,
    updateStatus: (id) => `admin/clubs/${id}/status`,
    facilities: (id) => `admin/clubs/${id}/facilities`,
    owners: () => `admin/clubs/owners`,
    sessions: (id) => `admin/clubs/${id}/sessions`,
    photos: (id) => `admin/clubs/${id}/photos`,
    uploadPhoto: (id) => `admin/clubs/${id}/photos/upload`,
    deletePhoto: (id, photoId) => `admin/clubs/${id}/photos/${photoId}`,
  },
  facilities: {
    list: 'admin/facilities',
    create: 'admin/facilities/add',
    get: (id) => `admin/facilities/${id}`,
    update: (id) => `admin/facilities/${id}`,
    delete: (id) => `admin/facilities/${id}`,
    canDeactivate: (id) => `admin/facilities/${id}/can-deactivate`,
    sessions: (id) => `admin/facilities/${id}/sessions`,
    availability: (id) => `admin/facilities/${id}/availability`,
    operatingDays: (id) => `admin/facilities/${id}/operating-days`,
    updateAvailability: (id) => `admin/facilities/availability/${id}`,
    deleteAvailability: (id) => `admin/facilities/availability/${id}`,
    unavailability: {
      list: (id) => `admin/facilities/${id}/unavailability`,
      create: (id) => `admin/facilities/${id}/unavailability`,
      update: (id) => `admin/facilities/unavailability/${id}`,
      delete: (id) => `admin/facilities/unavailability/${id}`,
    },
    photos: (id) => `admin/facilities/${id}/photos`,
    photosMultiple: (id) => `admin/facilities/${id}/photos/multiple`,
    deletePhoto: (id, photoId) => `admin/facilities/${id}/photos/${photoId}`,
    courts: {
      list: (facilityId) => `admin/facilities/${facilityId}/courts`,
      create: (facilityId) => `admin/facilities/${facilityId}/courts`,
      update: (courtId) => `admin/courts/${courtId}`,
      delete: (courtId) => `admin/courts/${courtId}`,
      get: (courtId) => `admin/courts/${courtId}`,
    },
    config: {
      get: (id) => `admin/facilities/${id}/config`,
      update: (id) => `admin/facilities/${id}/config`,
    },
  },
  sessions: {
    list: 'admin/sessions',
    create: 'admin/sessions',
    get: (id) => `admin/sessions/${id}`,
    update: (id) => `admin/sessions/${id}`,
    updateStatus: (id) => `admin/sessions/${id}/status`,
    delete: (id) => `admin/sessions/${id}`,
    approveRequest: (id) => `admin/sessions/${id}/approve-request`,
    rejectRequest: (id) => `admin/sessions/${id}/reject-request`,
    updatePlayerStatus: (id) => `admin/sessions/${id}/update-player-status`,
    updatePlayerPayment: (id) => `admin/sessions/${id}/update-player-payment`,
    payments: (id) => `admin/sessions/${id}/payments`,
    verifyPayment: (id) => `admin/sessions/${id}/verify-payment`,
    refundPayment: (id, paymentId) => `admin/sessions/${id}/payments/${paymentId}/refund`,
    assignCourts: (id) => `admin/sessions/${id}/courts`,
    unassignCourts: (id) => `admin/sessions/${id}/courts`,
    addPlayer: (id) => `admin/sessions/${id}/add-player`,
    // Session-scoped player picker: the backend derives the facility from the session.
    availablePlayers: (id) => `admin/sessions/${id}/available-players`,
    addPlayers: (id) => `admin/sessions/${id}/players`,
    // Facility cancellation with automatic wallet refunds — a business operation,
    // never a plain status edit (the backend rejects PUT { status: 'CANCELLED' }).
    cancellationPreview: (id) => `admin/sessions/${id}/cancellation-preview`,
    cancel: (id) => `admin/sessions/${id}/cancel`,
  },
  recurringSessions: {
    list: 'admin/sessions/recurring',
    create: 'admin/sessions/recurring',
    get: (id) => `admin/sessions/recurring/${id}`,
    update: (id) => `admin/sessions/recurring/${id}`,
    delete: (id) => `admin/sessions/recurring/${id}`,
    pause: (id) => `admin/sessions/recurring/${id}/pause`,
    resume: (id) => `admin/sessions/recurring/${id}/resume`,
    cancelFutureInstances: (id) => `admin/sessions/recurring/${id}/cancel-future-instances`,
  },
  matches: {
    list: 'admin/matches',
    create: 'admin/matches',
    get: (id) => `admin/matches/${id}`,
    update: (id) => `admin/matches/${id}`,
    delete: (id) => `admin/matches/${id}`,
    updateScore: (matchId) => `admin/matches/${matchId}/score`,
    matchmaking: 'admin/matches/matchmaking',
    sessionMatches: (sessionId) => `admin/matches/session/${sessionId}`,
    regenerateMatches: (sessionId) => `admin/matches/session/${sessionId}/regenerate`,
    deleteSessionMatches: (sessionId) => `admin/matches/session/${sessionId}`,
    nextRound: (sessionId) => `admin/matches/session/${sessionId}/next-round`,
  },
  appVersions: {
    list: '/app/version/all',
    create: '/app/version/add',
    update: (id) => `/app/version/update/${id}`,
    delete: (id) => `/app/version/delete/${id}`,
    get: (id) => `/app/version/${id}`,
  },
  banners: {
    list: 'admin/banners',
    active: 'admin/banners/active',
    create: 'admin/banners',
    get: (id) => `admin/banners/${id}`,
    update: (id) => `admin/banners/${id}`,
    delete: (id) => `admin/banners/${id}`,
  },
  users: {
    list: '/admin/users',
    get: (id) => `/admin/users/${id}`,
    update: (id) => `/admin/users/${id}`,
    delete: (id) => `/admin/users/${id}`,
  },
  bookings: {
    list: '/admin/bookings',
    create: '/admin/bookings',
    get: (id) => `/admin/bookings/${id}`,
    update: (id) => `/admin/bookings/${id}`,
    delete: (id) => `/admin/bookings/${id}`,
    updateStatus: (id) => `/admin/bookings/${id}/status`,
    confirm: (id) => `/admin/bookings/${id}/status/confirm`,
    cancel: (id) => `/admin/bookings/${id}/status/cancel`,
    rebook: (id) => `/admin/bookings/${id}/rebook`,
    complete: (id) => `/admin/bookings/${id}/complete`,
    noShow: (id) => `/admin/bookings/${id}/no-show`,
    updatePayment: (id) => `/admin/bookings/${id}/payment`,
    refund: (id) => `/admin/bookings/${id}/refund`,
    userBookings: (userId) => `/admin/bookings/users/${userId}/bookings`,
    analytics: '/admin/bookings/analytics',
    report: '/admin/bookings/report',
    bulkUpdate: '/admin/bookings/bulk-update',
    bulkDelete: '/admin/bookings/bulk-delete',
    checkAvailability: '/admin/bookings/check-availability',
    export: '/admin/bookings/export',
  },
  reports: {
    userActivity: '/admin/reports/user-activity',
    clubPerformance: '/admin/reports/club-performance',
    revenueAnalytics: '/admin/reports/revenue-analytics',
    systemUsage: '/admin/reports/system-usage',
  },
  analytics: {
    dashboard: '/admin/reports/analytics/dashboard',
    userActivity: '/admin/reports/analytics/user-activity',
    platformUsage: '/admin/reports/analytics/platform-usage',
    clubPerformance: '/admin/reports/analytics/club-performance',
    sessionTypes: '/admin/reports/analytics/session-types',
    facilityUsage: '/admin/reports/analytics/facility-usage',
    capacity: '/admin/reports/analytics/capacity',
    revenueBreakdown: '/admin/reports/analytics/revenue-breakdown',
    topPlayers: '/admin/reports/analytics/top-players',
    recentActivity: '/admin/reports/analytics/recent-activity',
  },
  payments: {
    list: '/admin/payments',
    stats: '/admin/payments/stats',
    revenueSummary: '/admin/payments/analytics/summary',
    revenueAnalytics: '/admin/payments/analytics',
    get: (id) => `/admin/payments/${id}`,
    refund: (id) => `/admin/payments/${id}/refund`,
    updateStatus: (id) => `/admin/payments/${id}/status`,
  },
  wallets: {
    allTransactions: '/admin/wallets/transactions',
    transaction: (id) => `/admin/wallets/transactions/${id}`,
    get: (userId) => `/admin/wallets/${userId}`,
    transactions: (userId) => `/admin/wallets/${userId}/transactions`,
    credit: (userId) => `/admin/wallets/${userId}/credit`,
    debit: (userId) => `/admin/wallets/${userId}/debit`,
    reconcile: (userId) => `/admin/wallets/${userId}/reconcile`,
  },
  vouchers: {
    campaigns: '/admin/vouchers/campaigns',
    campaign: (id) => `/admin/vouchers/campaigns/${id}`,
    publish: (id) => `/admin/vouchers/campaigns/${id}/publish`,
    pause: (id) => `/admin/vouchers/campaigns/${id}/pause`,
    resume: (id) => `/admin/vouchers/campaigns/${id}/resume`,
    cancel: (id) => `/admin/vouchers/campaigns/${id}/cancel`,
    archive: (id) => `/admin/vouchers/campaigns/${id}/archive`,
    duplicate: (id) => `/admin/vouchers/campaigns/${id}/duplicate`,
    assign: (id) => `/admin/vouchers/campaigns/${id}/assign`,
    campaignVouchers: (id) => `/admin/vouchers/campaigns/${id}/vouchers`,
    campaignRedemptions: (id) => `/admin/vouchers/campaigns/${id}/redemptions`,
    campaignAnalytics: (id) => `/admin/vouchers/campaigns/${id}/analytics`,
    revoke: (voucherId) => `/admin/vouchers/vouchers/${voucherId}/revoke`,
    redemptions: '/admin/vouchers/redemptions',
    analyticsSummary: '/admin/vouchers/analytics/summary',
    playerSearch: '/admin/vouchers/players/search',
    playerVouchers: (userId) => `/admin/vouchers/players/${userId}/vouchers`,
    redeemLookup: '/admin/vouchers/redeem/lookup',
    redeem: '/admin/vouchers/redeem',
  },
  leaderboards: {
    list: '/admin/leaderboards',
    dashboard: '/admin/leaderboards/dashboard',
    get: (id) => `/admin/leaderboards/${id}`,
    update: (id) => `/admin/leaderboards/${id}`,
    delete: (id) => `/admin/leaderboards/${id}`,
    export: '/admin/leaderboards/export',
  },
  // Manager Management APIs
  managers: {
    list: '/admin/users?role=MANAGER',
    create: '/admin/users',
    get: (id) => `/admin/users/${id}`,
    update: (id) => `/admin/users/${id}`,
    delete: (id) => `/admin/users/${id}`,
    updateStatus: (id) => `/admin/users/${id}/status`,
    resetPassword: (id) => `/admin/users/${id}/reset-password`,
  },
  managerAssignments: {
    list: '/admin/manager-assignments',
    create: '/admin/manager-assignments',
    delete: (id) => `/admin/manager-assignments/${id}`,
    getByUserId: (userId) => `/admin/manager-assignments?user_id=${userId}`,
    getByClubId: (clubId) => `/admin/manager-assignments?club_id=${clubId}`,
  },
  // Facility Owner Management APIs
  facilityOwners: {
    list: '/admin/facility-owners',
    create: '/admin/facility-owners',
    get: (id) => `/admin/facility-owners/${id}`,
    update: (id) => `/admin/facility-owners/${id}`,
    delete: (id) => `/admin/facility-owners/${id}`,
    activate: (id) => `/admin/facility-owners/${id}/activate`,
    suspend: (id) => `/admin/facility-owners/${id}/suspend`,
  },
  // Manager Console APIs (scoped endpoints)
  managerConsole: {
    dashboard: '/manager/dashboard',
    facilities: '/manager/facilities',
    sessions: '/manager/sessions',
    bookings: '/manager/bookings',
    reports: '/manager/reports',
  },
  feedback: {
    list: '/admin/feedbacks',
    get: (id) => `/admin/feedbacks/${id}`,
    export: '/admin/feedbacks/export',
  },
  bugs: {
    list: '/admin/reports/bugs',
    get: (id) => `/admin/reports/bugs/${id}`,
    create: '/admin/reports/bugs',
    update: (id) => `/admin/reports/bugs/${id}`,
    delete: (id) => `/admin/reports/bugs/${id}`,
    resolve: (id) => `/admin/reports/bugs/${id}/resolve`,
    close: (id) => `/admin/reports/bugs/${id}/close`,
  },
  supportTickets: {
    list: '/admin/supports/tickets',
    get: (id) => `/admin/supports/tickets/${id}`,
    create: '/admin/supports/tickets',
    update: (id) => `/admin/supports/tickets/${id}`,
    delete: (id) => `/admin/supports/tickets/${id}`,
    messages: (id) => `/admin/supports/tickets/${id}/messages`,
    postMessage: (id) => `/admin/supports/tickets/${id}/messages`,
    close: (id) => `/admin/supports/tickets/${id}/close`,
    resolve: (id) => `/admin/supports/tickets/${id}/resolve`,
  },
  notifications: {
    list: '/admin/notifications',
    stats: '/admin/notifications/stats',
    get: (id) => `/admin/notifications/${id}`,
    markAsRead: (id) => `/admin/notifications/${id}/read`,
    markAllAsRead: '/admin/notifications/mark-all-read',
    delete: (id) => `/admin/notifications/${id}`,
    create: '/admin/notifications',
  },
  openPlay: {
    toggleBreak: (sessionId, userId) => `admin/sessions/${sessionId}/players/${userId}/break`,
    updatePlayerStatus: (sessionId, userId) => `admin/sessions/${sessionId}/players/${userId}/status`,
    playerStats: (sessionId) => `admin/sessions/${sessionId}/players/stats`,
    completeAndRotate: (sessionId, matchId) => `admin/sessions/${sessionId}/matches/${matchId}/complete-and-rotate`,
    queueStatus: (sessionId) => `admin/sessions/${sessionId}/queue-status`,
    promoteFromQueue: (sessionId) => `admin/sessions/${sessionId}/promote-from-queue`,
    addToQueue: (sessionId) => `admin/sessions/${sessionId}/queue/add`,
    removeFromQueue: (sessionId) => `admin/sessions/${sessionId}/queue/remove`,
    reorderQueue: (sessionId) => `admin/sessions/${sessionId}/queue/reorder`,
    createQueueCourt: (sessionId) => `admin/sessions/${sessionId}/queue-courts`,
    deleteQueueCourt: (sessionId, qcId) => `admin/sessions/${sessionId}/queue-courts/${qcId}`,
    openPlayStats: (sessionId) => `admin/sessions/${sessionId}/open-play-stats`,
    resetStats: (sessionId) => `admin/sessions/${sessionId}/open-play-stats/reset`,
    // Ready pool → Smart / Manual rotation → match → result
    rotationState: (sessionId) => `admin/sessions/${sessionId}/open-play/rotation-state`,
    startRotation: (sessionId) => `admin/sessions/${sessionId}/open-play/rotation/start`,
    createSmartQueue: (sessionId) => `admin/sessions/${sessionId}/open-play/queue/smart`,
    createQueue: (sessionId) => `admin/sessions/${sessionId}/open-play/queue`,
    setQueuePlayers: (sessionId, matchId) =>
      `admin/sessions/${sessionId}/open-play/queue/${matchId}/players`,
    assignQueue: (sessionId, matchId) =>
      `admin/sessions/${sessionId}/open-play/queue/${matchId}/assign`,
    removeMatch: (sessionId, matchId) =>
      `admin/sessions/${sessionId}/open-play/matches/${matchId}`,
    clearCourt: (sessionId, courtId) =>
      `admin/sessions/${sessionId}/open-play/courts/${courtId}/clear`,
    createMatch: (sessionId) => `admin/sessions/${sessionId}/open-play/matches`,
    completeMatch: (sessionId, matchId) =>
      `admin/sessions/${sessionId}/open-play/matches/${matchId}/complete`,
    resendNotification: (sessionId, matchId) =>
      `admin/sessions/${sessionId}/open-play/matches/${matchId}/notifications/resend`,
    setAvailability: (sessionId, userId) =>
      `admin/sessions/${sessionId}/players/${userId}/availability`,
  },
  monitors: {
    list: 'admin/monitors',
    create: 'admin/monitors',
    get: (id) => `admin/monitors/${id}`,
    update: (id) => `admin/monitors/${id}`,
    delete: (id) => `admin/monitors/${id}`,
    regenerateToken: (id) => `admin/monitors/${id}/regenerate-token`,
    schedules: (id) => `admin/monitors/${id}/schedules`,
    courtCall: (id) => `admin/monitors/${id}/court-call`,
  },
  ads: {
    list: 'admin/ads',
    create: 'admin/ads',
    upload: 'admin/ads/upload',
    get: (id) => `admin/ads/${id}`,
    stats: (id) => `admin/ads/${id}/stats`,
    update: (id) => `admin/ads/${id}`,
    delete: (id) => `admin/ads/${id}`,
    playlists: {
      list: 'admin/ads/playlists/list',
      create: 'admin/ads/playlists',
      get: (id) => `admin/ads/playlists/${id}`,
      update: (id) => `admin/ads/playlists/${id}`,
      delete: (id) => `admin/ads/playlists/${id}`,
      addAd: (id) => `admin/ads/playlists/${id}/ads`,
      removeAd: (id, adId) => `admin/ads/playlists/${id}/ads/${adId}`,
      updateAd: (id, adId) => `admin/ads/playlists/${id}/ads/${adId}`,
      reorder: (id) => `admin/ads/playlists/${id}/reorder`,
    },
    schedules: {
      list: 'admin/ads/schedules/list',
      create: 'admin/ads/schedules',
      get: (id) => `admin/ads/schedules/${id}`,
      update: (id) => `admin/ads/schedules/${id}`,
      delete: (id) => `admin/ads/schedules/${id}`,
      assignMonitor: (scheduleId, monitorId) => `admin/ads/schedules/${scheduleId}/monitors/${monitorId}`,
      removeMonitor: (scheduleId, monitorId) => `admin/ads/schedules/${scheduleId}/monitors/${monitorId}`,
    },
    impressions: {
      record: 'admin/ads/impressions',
      stats: 'admin/ads/impressions/stats',
    },
  },
  chat: {
    inboxes: {
      provision: 'admin/chat/inboxes',
    },
    conversations: (facilityId) => `admin/chat/facilities/${facilityId}/conversations`,
    messages: (conversationId) => `admin/chat/conversations/${conversationId}/messages`,
    sendMessage: (conversationId) => `admin/chat/conversations/${conversationId}/messages`,
    updateStatus: (conversationId) => `admin/chat/conversations/${conversationId}/status`,
    presence: 'chat/presence',
  },
  display: {
    content: 'display/content',
    acknowledge: 'display/acknowledge',
    monitor: 'display/monitor',
  },
};

